import type { Bi } from 'schools/vastu/types/bilingual';

export type VastuTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type VastuChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: VastuTopic[];
};

export const VASTU_HERO = {
  eyebrow: { en: 'Sthapatya · Space harmony', hi: 'स्थापत्य · स्थान सामंजस्य' },
  titleShort: { en: 'Vastu Shastra', hi: 'वास्तु शास्त्र' },
  title: { en: 'Vastu Shastra', hi: 'वास्तु शास्त्र' },
  motto: {
    en: 'Align built space with directional energies - plot, pada, room, and muhurta together.',
    hi: 'निर्मित स्थान को दिशाओं की ऊर्जा से मिलाएँ - प्लॉट, पद, कक्ष और मुहूर्त एक साथ।',
  },
  tagline: {
    en: 'Fifty-six lessons with a compass on every page - mandala, zones, padas, apartments, and floor-plan practice.',
    hi: 'छप्पन पाठ - हर पृष्ठ पर कम्पास: मंडल, क्षेत्र, पद, फ्लैट और फ्लोर प्लान अभ्यास।',
  },
  intro: {
    en: 'Pair with Panchang for timing and Surya Siddhanta for orientation - this school teaches where functions belong in space.',
    hi: 'समय हेतु पंचांग और दिशा हेतु सिद्धान्त के साथ - यह स्कूल बताता है स्थान में क्या कहाँ रखें।',
  },
  trait: {
    en: 'Direction + zone + pada = harmonious space',
    hi: 'दिशा + क्षेत्र + पद = सामंजस्यपूर्ण स्थान',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): VastuTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const VASTU_CHAPTERS: VastuChapter[] = [
  {
    id: 'foundations',
    number: 1,
    title: { en: 'Foundations', hi: 'आधार' },
    subtitle: { en: 'Elements, directions, and Panchang bridge', hi: 'तत्व, दिशाएँ और पंचांग कड़ी' },
    icon: '🏛️',
    topics: [
      topic('what-is-vastu', 'What Is Vastu?', 'वास्तु क्या है?', 'Space-time harmony in built form.', 'निर्मित रूप में स्थान-काल सामंजस्य।', 12, '🏠'),
      topic('pancha-bhoota-five-elements', 'Pancha Bhoota', 'पंच भूत', 'Five elements on the compass.', 'दिशा-चक्र पर पाँच तत्व।', 12, '🌀'),
      topic('eight-sixteen-thirty-two-directions', '8 → 16 → 32 Directions', '8 → 16 → 32 दिशाएँ', 'From dik to pada resolution.', 'दिक से पद तक विस्तार।', 12, '🧭'),
      topic('vastu-vs-feng-shui', 'Vastu vs Feng Shui', 'वास्तु बनाम फेंग शुई', 'Two frames, similar intent.', 'दो परिप्रेक्ष्य, समान उद्देश्य।', 12, '⚖️'),
      topic('bridge-from-panchang-muhurta', 'Bridge from Panchang', 'पंचांग से कड़ी', 'Bhoomi pujan and griha pravesh timing.', 'भूमि पूजन और गृह प्रवेश समय।', 12, '🔗'),
    ],
  },
  {
    id: 'purusha-mandala',
    number: 2,
    title: { en: 'Purusha & Mandala', hi: 'पुरुष और मंडल' },
    subtitle: { en: 'Vastu Purusha, devas, and the full chakra', hi: 'वास्तु पुरुष, देव और पूर्ण चक्र' },
    icon: '☸️',
    topics: [
      topic('vastu-purusha-legend', 'Vastu Purusha Legend', 'वास्तु पुरुष कथा', 'Body on the mandala.', 'मंडल पर शरीर।', 14, '🧘'),
      topic('brahmasthan-center', 'Brahmasthan', 'ब्रह्मस्थान', 'Sacred open center.', 'पवित्र खुला केंद्र।', 12, '✨'),
      topic('forty-five-deva-fields', '45 Deva Fields', '45 देव क्षेत्र', 'Deity ring overview.', 'देव मण्डल अवलोकन।', 14, '🕉️'),
      topic('reading-vastu-chakra', 'Reading the Chakra', 'चक्र पढ़ना', 'Layer by layer decode.', 'स्तर-दर-स्तर समझ।', 14, '📊'),
      topic('finding-your-center-point', 'Find the Center Point', 'केंद्र बिंदु खोजें', 'Diagonal method on any floor plan.', 'किसी भी योजना पर विकर्ण विधि।', 11, '🎯'),
      topic('sixteen-zones-on-plan', '16 Zones on Your Plan', 'योजना पर 16 क्षेत्र', 'Mark directional wedges from center.', 'केंद्र से दिशा खंड चिह्नित करें।', 12, '🗂️'),
      topic('compass-and-true-north', 'Compass & True North', 'कम्पास और सही उत्तर', 'Survey and orientation.', 'सर्वेक्षण और अभिमुखता।', 12, '🧭'),
      topic('mandala-on-your-plot', 'Mandala on Your Plot', 'प्लॉट पर मंडल', 'Place the mandala on your plot.', 'अपने प्लॉट पर मंडल रखें।', 15, '🗺️'),
    ],
  },
  {
    id: 'directions-deep',
    number: 3,
    title: { en: 'Directions in Depth', hi: 'दिशाएँ विस्तार से' },
    subtitle: { en: 'North through Southwest zones', hi: 'उत्तर से नैऋत्य तक' },
    icon: '🌐',
    topics: [
      topic('north-water-wealth', 'North - Wealth', 'उत्तर - धन', 'Kubera and opportunities.', 'कुबेर और अवसर।', 12, '💰'),
      topic('east-sun-growth', 'East - Growth', 'पूर्व - वृद्धि', 'Sunrise and social energy.', 'सूर्योदय और सामाजिक ऊर्जा।', 12, '🌅'),
      topic('south-fire-fame', 'South - Fame', 'दक्षिण - यश', 'Yama zone - careful use.', 'यम क्षेत्र - सावधान उपयोग।', 12, '🔥'),
      topic('west-air-gains', 'West - Gains', 'पश्चिम - लाभ', 'Completion and rest.', 'पूर्णता और विश्राम।', 12, '🌬️'),
      topic('northeast-ishanya', 'North-East - Ishanya', 'ईशान कोण', 'Sacred and water bias.', 'पवित्र और जल प्रवृत्ति।', 13, '🙏'),
      topic('southeast-fire-corner', 'South-East - Fire Corner', 'आग्नेय - अग्नि कोण', 'Kitchen and daily heat zone.', 'रसोई और दैनिक अग्नि क्षेत्र।', 12, '🍳'),
      topic('northwest-movement-zone', 'North-West - Movement', 'वायव्य - गति', 'Change, travel, and guests.', 'परिवर्तन, यात्रा और अतिथि।', 12, '🌀'),
      topic('nne-nnw-sub-zones', 'NNE & NNW Sub-Zones', 'NNE और NNW उप-क्षेत्र', 'Clarity and thought-flow wedges.', 'स्पष्टता और विचार-प्रवाह खंड।', 11, '💭'),
      topic('southwest-stability', 'South-West - Stability', 'नैऋत्य - स्थिरता', 'Earth and master zone.', 'पृथ्वी और स्वामी क्षेत्र।', 13, '🏔️'),
    ],
  },
  {
    id: 'residential-zones',
    number: 4,
    title: { en: 'Residential Zones', hi: 'आवासीय क्षेत्र' },
    subtitle: { en: 'Rooms and functions at home', hi: 'घर में कक्ष और कार्य' },
    icon: '🏡',
    topics: [
      topic('main-entrance-principles', 'Main Entrance', 'मुख्य द्वार', 'Energy gate of the home.', 'गृह का ऊर्जा द्वार।', 13, '🚪'),
      topic('kitchen-agneya-rules', 'Kitchen Rules', 'रसोई नियम', 'SE fire corner placement.', 'आग्नेय अग्नि कोण।', 14, '🍳'),
      topic('bedroom-southwest', 'Bedroom Placement', 'शयनकक्ष', 'SW stability and head direction.', 'नैऋत्य स्थिरता और शिर दिशा।', 13, '🛏️'),
      topic('prayer-pooja-room', 'Prayer Room', 'पूजा-घर', 'NE/E sacred zone.', 'ईशान/पूर्व पवित्र क्षेत्र।', 12, '🪔'),
      topic('toilet-bathroom-dosha', 'Toilet & Bathroom', 'शौचालय', 'Drain zones to avoid.', 'टालने योग्य निकास क्षेत्र।', 13, '🚿'),
      topic('staircase-mezzanine', 'Staircase', 'सीढ़ी', 'Clockwise and heavy sides.', 'दक्षिणावर्त और भारी दिशाएँ।', 12, '🪜'),
      topic('living-room-study-zones', 'Living & Study Rooms', 'बैठक और अध्ययन', 'Gathering and focus corners.', 'मिलन और एकाग्रता कोने।', 13, '📚'),
    ],
  },
  {
    id: 'padas-doors',
    number: 5,
    title: { en: 'Padas & Doors', hi: 'पद और द्वार' },
    subtitle: { en: '32 padas and entrance selection', hi: '32 पद और प्रवेश चयन' },
    icon: '🚪',
    topics: [
      topic('thirty-two-padas-overview', '32 Padas Overview', '32 पद अवलोकन', 'N1–W8 pada ring.', 'N1–W8 पद वलय।', 13, '⭕'),
      topic('auspicious-entrance-padas', 'Auspicious Entrances', 'शुभ प्रवेश पद', 'Plus/minus pada table.', 'प्लस/माइनस पद तालिका।', 14, '✅'),
      topic('windows-ventilation', 'Windows & Airflow', 'खिड़की और वायु', 'Light and ventilation map.', 'प्रकाश और वेंटिलेशन मानचित्र।', 12, '🪟'),
      topic('gate-road-approach', 'Gate & Road', 'द्वार और सड़क', 'Approach vectors and veedhi.', 'आगमन दिशा और वीधि।', 13, '🛣️'),
      topic('entrance-muhurta-checklist', 'Entrance Muhurta', 'प्रवेश मुहूर्त', 'Vastu pada + Panchang row.', 'वास्तु पद + पंचांग पंक्ति।', 13, '📋'),
    ],
  },
  {
    id: 'shapes-site',
    number: 6,
    title: { en: 'Shapes & Site', hi: 'आकार और भूखंड' },
    subtitle: { en: 'Plot form, water, and neighbours', hi: 'प्लॉट रूप, जल और पड़ोस' },
    icon: '📐',
    topics: [
      topic('plot-shapes-rectangle-best', 'Plot Shapes', 'भूखंड आकार', 'Square, rectangle, L, T.', 'वर्ग, आयत, L, T।', 12, '▭'),
      topic('cut-and-extension-dosha', 'Cut & Extension', 'कट और विस्तार', 'NE cut and SW extension.', 'ईशान कट और नैऋत्य विस्तार।', 13, '✂️'),
      topic('slope-and-level', 'Slope & Level', 'ढाल और समतल', 'High SW, low NE.', 'उच्च नैऋत्य, निम्न ईशान।', 12, '📉'),
      topic('water-bore-tank', 'Water & Septic', 'जल और सेप्टिक', 'Tank and bore placement.', 'टैंक और बोर स्थान।', 13, '💧'),
      topic('neighbour-overlap-shadow', 'Neighbour Shadow', 'पड़ोस छाया', 'Tall blocks on NE/E.', 'ईशान/पूर्व पर ऊँची इमारत।', 12, '🏢'),
    ],
  },
  {
    id: 'commercial',
    number: 7,
    title: { en: 'Commercial & Office', hi: 'व्यापार और कार्यालय' },
    subtitle: { en: 'Shops, offices, clinics', hi: 'दुकान, कार्यालय, क्लिनिक' },
    icon: '🏢',
    topics: [
      topic('shop-showroom-layout', 'Shop Layout', 'दुकान लेआउट', 'Entrance, display, cash.', 'प्रवेश, प्रदर्शन, कैश।', 13, '🛍️'),
      topic('office-cabin-desk', 'Office & Desk', 'कार्यालय मेज', 'Owner SW, staff zones.', 'स्वामी नैऋत्य, कर्मचारी क्षेत्र।', 13, '💼'),
      topic('factory-warehouse', 'Factory Basics', 'कारखाना आधार', 'Heavy SW, fire SE.', 'भारी नैऋत्य, अग्नि आग्नेय।', 13, '🏭'),
      topic('clinic-hospital-basics', 'Clinic Basics', 'क्लिनिक आधार', 'Patient bed and clean zones.', 'रोगी बिस्तर और स्वच्छ क्षेत्र।', 12, '🏥'),
    ],
  },
  {
    id: 'practice-ethics',
    number: 8,
    title: { en: 'Practice & Ethics', hi: 'अभ्यास और नैतिकता' },
    subtitle: { en: 'Audit, remedies, and consultation', hi: 'ऑडिट, उपाय और परामर्श' },
    icon: '📋',
    topics: [
      topic('defect-awareness-not-fear', 'Defect Awareness', 'दोष जागरूकता', 'Audit without fear language.', 'भय भाषा के बिना ऑडिट।', 13, '🔍'),
      topic('three-step-floor-plan-read', '3-Step Plan Read', '3-चरण योजना पढ़ना', 'Structure, activity, long-term impact.', 'संरचना, गतिविधि, दीर्घकालिक प्रभाव।', 13, '📑'),
      topic('writing-a-vastu-report', 'Writing a Vastu Report', 'वास्तु रिपोर्ट लेखन', 'Calm language and priority order.', 'शांत भाषा और प्राथमिकता क्रम।', 14, '✍️'),
      topic('non-destructive-remedies', 'Non-Destructive Remedies', 'गैर-विनाशकारी उपाय', 'Color, plant, symbol ethics.', 'रंग, पौधा, प्रतीक नैतिकता।', 12, '🌿'),
      topic('floor-plan-audit-workshop', 'Floor Plan Workshop', 'फ्लोर प्लान कार्यशाला', 'Place rooms on your floor plan.', 'फ्लोर प्लान पर कक्ष रखें।', 15, '📐'),
      topic('client-consultation-workflow', 'Client Workflow', 'ग्राहक कार्यप्रवाह', 'From first visit to written report.', 'पहली मुलाकात से लिखित रिपोर्ट तक।', 14, '📝'),
      topic('vastu-capstone-case-study', 'Full Home Case Study', 'पूर्ण घर का उदाहरण', 'Full home case from start to finish.', 'शुरू से अंत तक पूरा घर का उदाहरण।', 16, '🎓'),
    ],
  },
  {
    id: 'home-in-practice',
    number: 9,
    title: { en: 'Your Home Speaks', hi: 'आपका घर बोलता है' },
    subtitle: { en: 'Signals, flats, cases, and modern life', hi: 'संकेत, फ्लैट, केस और आधुनिक जीवन' },
    icon: '🏘️',
    topics: [
      topic('home-energy-warning-signs', 'Early Warning Signs', 'प्रारंभिक संकेत', 'When a space feels off before it breaks.', 'टूटने से पहले जब स्थान अजीब लगे।', 12, '👂'),
      topic('vastu-in-small-spaces', 'Vastu in Small Spaces', 'छोटे स्थान में वास्तु', 'Apartments and compact flats.', 'अपार्टमेंट और कॉम्पैक्ट फ्लैट।', 12, '🏢'),
      topic('nine-pillars-of-home-energy', '9 Pillars of Home Energy', '9 ऊर्जा स्तंभ', 'What really holds a home together.', 'घर को वास्तव में क्या जोड़ता है।', 14, '🏛️'),
      topic('real-home-case-studies', 'Real Home Stories', 'वास्तविक घर की कहानियाँ', 'Kitchen and entrance fixes that worked.', 'रसोई और द्वार सुधार जो काम आए।', 14, '📖'),
      topic('pre-buy-home-checklist', 'Pre-Buy Checklist', 'खरीद से पहले चेकलिस्ट', 'Inspect before you sign.', 'हस्ताक्षर से पहले जाँचें।', 13, '✅'),
      topic('vastu-meets-modern-design', 'Vastu Meets Modern Design', 'वास्तु और आधुनिक डिज़ाइन', 'Glass, open plans, and harmony.', 'ग्लास, ओपन प्लान और सामंजस्य।', 13, '🏗️'),
    ],
  },
];

export function getVastuTopic(id: string) {
  for (const chapter of VASTU_CHAPTERS) {
    const t = chapter.topics.find((x) => x.id === id);
    if (t) return { topic: t, chapter };
  }
  return null;
}

export function getVastuChapter(chapterId: string): VastuChapter | undefined {
  return VASTU_CHAPTERS.find((c) => c.id === chapterId);
}

export function getVastuChapterIndex(chapterId: string): number {
  return VASTU_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getVastuTopicCount(): number {
  return VASTU_CHAPTERS.reduce((n, c) => n + c.topics.length, 0);
}

export function getVastuTopicNeighbors(topicId: string): {
  prev: VastuTopic | null;
  next: VastuTopic | null;
} {
  const flat = VASTU_CHAPTERS.flatMap((c) => c.topics);
  const idx = flat.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return { prev: flat[idx - 1] ?? null, next: flat[idx + 1] ?? null };
}

export const VASTU_TOPIC_IDS = VASTU_CHAPTERS.flatMap((c) => c.topics.map((t) => t.id));
